import React, { useState } from 'react'
import useStore from '../hooks/useStore'

const INITIAL_FORM = {
  type: 'feedback',
  priority: 'normal',
  email: '',
  phone: '',
  title: '',
  details: '',
  pagePath: typeof window !== 'undefined' ? window.location.pathname : '',
  contactOk: true,
}

function FeedbackPanel() {
  const { user, submitFeedbackReport, showToast } = useStore()
  const [form, setForm] = useState({
    ...INITIAL_FORM,
    email: user?.email || '',
  })
  const [sending, setSending] = useState(false)

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }))
  }

  const handleSubmit = async (event) => {
    event.preventDefault()

    if (!form.title.trim() || !form.details.trim()) {
      showToast('⚠', 'Add a title and details first')
      return
    }

    setSending(true)
    try {
      await submitFeedbackReport(form)
      showToast('✓', form.type === 'issue' ? 'Issue logged. Thank you.' : 'Feedback sent. Thank you.')
      setForm({
        ...INITIAL_FORM,
        email: user?.email || '',
        pagePath: typeof window !== 'undefined' ? window.location.pathname : '',
      })
    } catch (error) {
      const missingTable = /relation .*feedback_reports|feedback_reports.*does not exist/i.test(error?.message || '')
      showToast('✗', missingTable ? 'Feedback table missing in Supabase' : (error?.message || 'Could not send report'))
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="panel active">
      <div className="pg-ey">Support · Product Signal</div>
      <div className="pg-ti">Feedback &amp; <em>Issue Logging</em></div>
      <div className="pg-su">
        Share what is working, what feels unclear, or anything that blocked your flow inside ResultOS.
      </div>

      <div className="feedback-layout">
        <form className="card feedback-form" onSubmit={handleSubmit}>
          <div className="cg azure" />
          <div className="c-lbl">Create Report</div>

          <div className="feedback-segment" role="tablist" aria-label="Report type">
            {[
              { value: 'feedback', label: 'Feedback' },
              { value: 'issue', label: 'Issue' },
              { value: 'idea', label: 'Idea' },
            ].map((option) => (
              <button
                key={option.value}
                type="button"
                className={`feedback-segment-btn${form.type === option.value ? ' active' : ''}`}
                onClick={() => updateField('type', option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>

          <div className="g2 mb20">
            <div>
              <label className="a-lbl" htmlFor="feedback-priority">Priority</label>
              <select
                id="feedback-priority"
                className="sel"
                value={form.priority}
                onChange={(event) => updateField('priority', event.target.value)}
              >
                <option value="normal">Normal</option>
                <option value="important">Important</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div>
              <label className="a-lbl" htmlFor="feedback-path">Page / Area</label>
              <input
                id="feedback-path"
                type="text"
                value={form.pagePath}
                onChange={(event) => updateField('pagePath', event.target.value)}
                placeholder="/community-live"
              />
            </div>
          </div>

          <div className="g2 mb20">
            <div>
              <label className="a-lbl" htmlFor="feedback-email">Email ID</label>
              <input
                id="feedback-email"
                type="email"
                value={form.email}
                onChange={(event) => updateField('email', event.target.value)}
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="a-lbl" htmlFor="feedback-phone">Phone Number Optional</label>
              <input
                id="feedback-phone"
                type="text"
                value={form.phone}
                onChange={(event) => updateField('phone', event.target.value)}
                placeholder="+1 555 010 1234"
              />
            </div>
          </div>

          <label className="a-lbl" htmlFor="feedback-title">Short Title</label>
          <input
            id="feedback-title"
            type="text"
            value={form.title}
            onChange={(event) => updateField('title', event.target.value)}
            placeholder="e.g. Live map feels too large on mobile"
            maxLength="120"
          />

          <label className="a-lbl" htmlFor="feedback-details">Details</label>
          <textarea
            id="feedback-details"
            className="ta-expand feedback-details"
            rows="7"
            value={form.details}
            onChange={(event) => updateField('details', event.target.value)}
            placeholder="Tell us what happened, what you expected, or what would make this better."
          />

          <label className="feedback-check">
            <input
              type="checkbox"
              checked={form.contactOk}
              onChange={(event) => updateField('contactOk', event.target.checked)}
            />
            <span>You may contact me about this report if needed.</span>
          </label>

          <div className="feedback-actions">
            <button className="btn bg-gold" type="submit" disabled={sending}>
              {sending ? 'Sending...' : 'Submit Report'}
            </button>
          </div>
        </form>

        <aside className="feedback-side">
          <div className="feedback-side-block">
            <div className="feedback-side-kicker">Best Reports Include</div>
            <div className="feedback-tip">Where you were, what you tried, and what happened next.</div>
          </div>
          <div className="feedback-side-block">
            <div className="feedback-side-kicker">For Issues</div>
            <div className="feedback-tip">Mention device, browser, and whether it happens every time.</div>
          </div>
          <div className="feedback-side-block feedback-side-emphasis">
            <div className="feedback-side-kicker">Product Promise</div>
            <div className="feedback-tip">Every serious signal helps make ResultOS sharper for high-performance users.</div>
          </div>
        </aside>
      </div>
    </div>
  )
}

export default FeedbackPanel
